import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const root = window.Shopify?.routes?.root || '/';
const thresholdCents = 7500;
let mutationInProgress = false;
let activeThemeCartUpdates = 0;

async function requestCart(path, payload) {
  const response = await fetch(`${root}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error('cart request failed');
  return response.json();
}

async function getCart() {
  const response = await fetch(`${root}cart.js`);
  if (!response.ok) throw new Error('cart request failed');
  return response.json();
}

function sectionIds() {
  return [...new Set(
    [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((element) => element.dataset.sectionId)
      .filter(Boolean)
  )];
}

function updateTheme(cart, sections, action, variantId) {
  document.dispatchEvent(new CartLinesUpdateEvent({
    action,
    context: 'cart',
    lines: [{ id: String(variantId), quantity: action === 'remove' ? 0 : 1 }],
    promise: Promise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections,
        items: cart.items,
        itemCount: cart.item_count,
        source: 'gift-drawer-promo',
        didError: false,
      },
    }),
  }));
}

function isLegacyGift(item) {
  return item.properties?._free_gift === 'true' ||
    (item.product_type === 'Seifenfarbe' && item.variant_title === '10 ml' &&
      item.original_price > 0 && item.final_price === 0);
}

function qualifiedSubtotal(cart) {
  return cart.items.reduce((total, item) => total +
    (isLegacyGift(item) ? 0 : item.original_line_price), 0);
}

function isGift(item) {
  return (item.product_type === 'Aktionsgeschenk' && item.original_price === 0) ||
    isLegacyGift(item);
}

function selectedGift(cart) {
  return cart.items.find(isGift);
}

function sectionPayload() {
  return { sections: sectionIds().join(','), sections_url: window.location.pathname };
}

async function removeInvalidGift() {
  if (mutationInProgress || !document.querySelector('[data-gift-drawer-promo], [data-free-gift-picker]')) return;
  mutationInProgress = true;
  try {
    const cart = await getCart();
    const gift = selectedGift(cart);
    if (!gift || qualifiedSubtotal(cart) >= thresholdCents) return;

    const result = await requestCart('cart/change.js', {
      id: gift.key,
      quantity: 0,
      ...sectionPayload(),
    });
    updateTheme(await getCart(), result.sections, 'remove', gift.variant_id);
    if (window.location.pathname.replace(/\/$/, '') === `${root}cart`.replace(/\/$/, '')) {
      window.location.reload();
    }
  } catch (error) {
    console.warn('[gift-drawer-promo] Could not remove an ineligible gift:', error);
  } finally {
    mutationInProgress = false;
  }
}

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-gift-choose]');
  if (!button || mutationInProgress) return;

  const promo = button.closest('[data-gift-drawer-promo]');
  const choice = promo?.querySelector('[data-gift-choice]');
  const error = promo?.querySelector('[data-gift-error]');
  const variantId = choice?.value;
  if (!variantId) {
    if (error) {
      error.textContent = 'Bitte wähle zuerst ein Geschenk aus.';
      error.hidden = false;
    }
    return;
  }

  mutationInProgress = true;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  if (error) error.hidden = true;

  let previousGift;
  let removedPreviousGift = false;
  try {
    const cart = await getCart();
    if (qualifiedSubtotal(cart) < thresholdCents) {
      throw new Error('threshold not met');
    }
    previousGift = selectedGift(cart);
    if (String(previousGift?.variant_id) === String(variantId)) return;

    if (previousGift) {
      await requestCart('cart/change.js', { id: previousGift.key, quantity: 0 });
      removedPreviousGift = true;
    }

    const result = await requestCart('cart/add.js', {
      items: [{ id: Number(variantId), quantity: 1 }],
      ...sectionPayload(),
    });
    updateTheme(await getCart(), result.sections, 'add', variantId);
  } catch (failure) {
    if (removedPreviousGift && previousGift) {
      try {
        await requestCart('cart/add.js', {
          items: [{
            id: previousGift.variant_id,
            quantity: 1,
            ...(previousGift.properties?._free_gift === 'true' ? { properties: { _free_gift: 'true' } } : {}),
          }],
        });
      } catch (restoreFailure) {
        console.warn('[gift-drawer-promo] Could not restore the previous gift:', restoreFailure);
      }
    }
    if (error) {
      error.textContent = 'Das Geschenk konnte nicht hinzugefügt werden. Bitte versuche es erneut.';
      error.hidden = false;
    }
  } finally {
    button.disabled = false;
    button.removeAttribute('aria-busy');
    mutationInProgress = false;
  }
});

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.promise) {
    activeThemeCartUpdates += 1;
    event.promise.finally(() => {
      activeThemeCartUpdates = Math.max(0, activeThemeCartUpdates - 1);
    }).catch(() => {});
  }
  event.promise?.then(({ detail }) => {
    if (detail?.source !== 'gift-drawer-promo') void removeInvalidGift();
  }).catch(() => {});
});

void removeInvalidGift();

const giftReminderDismissedKey = 'moldyland-gift-reminder-dismissed';
const dismissedThisPage = { value: false };
const resumedForms = new WeakSet();
const pendingGiftChecks = new WeakSet();

function reminderWasDismissed() {
  if (dismissedThisPage.value) return true;
  try {
    return window.sessionStorage.getItem(giftReminderDismissedKey) === 'true';
  } catch {
    return false;
  }
}

function rememberReminderDismissal() {
  dismissedThisPage.value = true;
  try {
    window.sessionStorage.setItem(giftReminderDismissedKey, 'true');
  } catch {
    // The in-memory flag keeps this page usable when storage is unavailable.
  }
}

function dialogForForm(form) {
  const drawer = form.closest('cart-drawer-component');
  if (drawer) return drawer.querySelector('[data-gift-reminder-dialog]');
  return [...document.querySelectorAll('[data-gift-reminder-dialog]')]
    .find((dialog) => !dialog.closest('cart-drawer-component')) || null;
}

function giftChooserFor(element) {
  const drawer = element.closest('cart-drawer-component');
  const drawerChoice = drawer?.querySelector('[data-gift-choice]');
  if (drawerChoice) return drawerChoice;

  const picker = document.querySelector('[data-free-gift-picker]');
  return picker?.querySelector('[data-gift-variant-id]:not(:disabled)') || null;
}

function goToGiftChooser(element) {
  const chooser = giftChooserFor(element);
  if (!chooser) return;
  chooser.scrollIntoView({ behavior: 'smooth', block: 'center' });
  requestAnimationFrame(() => chooser.focus({ preventScroll: true }));
}

function closeReminder(dialog, restoreFocus = true) {
  if (!dialog?.open) return;
  const trigger = dialog.__giftReminderTrigger;
  dialog.close();
  if (restoreFocus && trigger?.isConnected) requestAnimationFrame(() => trigger.focus());
}

function resumeCheckout(form, submitter) {
  resumedForms.add(form);
  if (submitter && submitter.form === form) form.requestSubmit(submitter);
  else form.requestSubmit();
}

async function cartNeedsGiftReminder() {
  const deadline = Date.now() + 3000;
  const giftSelectionIsBusy = () => Boolean(
    document.querySelector('[data-gift-variant-id][aria-busy="true"]')
  );
  while ((mutationInProgress || activeThemeCartUpdates > 0 || giftSelectionIsBusy()) &&
      Date.now() < deadline) {
    await new Promise((resolve) => window.setTimeout(resolve, 40));
  }
  const remaining = deadline - Date.now();
  if (remaining <= 0) return null;

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), remaining);
  try {
    const response = await fetch(`${root}cart.js`, { signal: controller.signal });
    if (!response.ok) return null;
    const cart = await response.json();
    return qualifiedSubtotal(cart) >= thresholdCents && !selectedGift(cart);
  } catch {
    return null;
  } finally {
    window.clearTimeout(timeout);
  }
}

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;
  if (resumedForms.has(form)) {
    resumedForms.delete(form);
    return;
  }

  const submitter = event.submitter;
  if (submitter && submitter.name !== 'checkout' && submitter.id !== 'checkout') return;
  if (reminderWasDismissed()) return;
  if (pendingGiftChecks.has(form)) {
    event.preventDefault();
    return;
  }

  const dialog = dialogForForm(form);
  if (dialog?.open) {
    event.preventDefault();
    return;
  }
  if (!dialog) return;

  event.preventDefault();
  pendingGiftChecks.add(form);
  const eligible = await cartNeedsGiftReminder();
  pendingGiftChecks.delete(form);
  if (eligible !== true) {
    resumeCheckout(form, submitter);
    return;
  }

  const availableReminder = dialogForForm(form);
  if (!availableReminder || availableReminder.open) {
    resumeCheckout(form, submitter);
    return;
  }
  availableReminder.__giftReminderForm = form;
  availableReminder.__giftReminderSubmitter = submitter;
  availableReminder.__giftReminderTrigger = submitter || form.querySelector('[name="checkout"]');
  availableReminder.showModal();
  availableReminder.querySelector('[data-gift-reminder-choose]')?.focus();
}, true);

document.addEventListener('click', (event) => {
  const choose = event.target.closest('[data-gift-reminder-choose]');
  if (choose) {
    event.preventDefault();
    const dialog = choose.closest('[data-gift-reminder-dialog]');
    if (dialog) closeReminder(dialog, false);
    goToGiftChooser(choose);
    return;
  }

  const close = event.target.closest('[data-gift-reminder-close]');
  if (close) {
    closeReminder(close.closest('[data-gift-reminder-dialog]'));
    return;
  }

  const continueWithoutGift = event.target.closest('[data-gift-reminder-continue]');
  if (continueWithoutGift) {
    const dialog = continueWithoutGift.closest('[data-gift-reminder-dialog]');
    const form = dialog?.__giftReminderForm;
    if (!form) {
      closeReminder(dialog);
      return;
    }
    const submitter = dialog.__giftReminderSubmitter;
    rememberReminderDismissal();
    closeReminder(dialog, false);
    resumeCheckout(form, submitter);
  }
});

document.addEventListener('click', (event) => {
  const dialog = event.target;
  if (dialog instanceof HTMLDialogElement &&
      dialog.matches('[data-gift-reminder-dialog]') && event.target === dialog) {
    closeReminder(dialog);
  }
});

document.addEventListener('cancel', (event) => {
  if (event.target instanceof HTMLDialogElement &&
      event.target.matches('[data-gift-reminder-dialog]')) {
    event.preventDefault();
    closeReminder(event.target);
  }
}, true);
